import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, styled, Link, Box, IconButton } from '@mui/material';
import { Transactions } from '../../models/dashboard/Transactions';
import { getPdfTransactions, getTransactions, getTransactionsDataFilter } from '../../hooks/actions/dashboard';
import { formatDate, formatDateDayMonthYear } from '../../helpers/FormatDate';
import { GRAY_LIGHT, GREEN_DARK, GREEN_LOGO, GREEN_MAIN } from '../../theme/colors';
import { formatToTwoDecimals } from '../../helpers/numberFormats';
import { useTranslationContext } from '../../hooks/translations/translationsProvider';
import settings from '../../utilities/settings';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PrintIcon from '@mui/icons-material/Print';

const TableCellDefault = styled(TableCell)(({ theme }) => ({
    backgroundColor: `transparent`, //`${GREEN_LOGO}20`,
    fontWeight:'bold',
    color: GREEN_MAIN,
    border:'0px',
    whiteSpace: 'nowrap',
}));

const TableCellRowsDefault = styled(TableCell)(({ theme }) => ({
    fontSize:'0.85rem',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    color: GREEN_MAIN
}));

const CustomTableContainer = styled(TableContainer)(({ theme }) => ({
    // Base scrollbar styling
    "&::-webkit-scrollbar": {
      height: "8px", // Height for horizontal scrollbar
      width: "8px", // Width for vertical scrollbar
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    // This is the key part - remove ALL scrollbar buttons/arrows
    "&::-webkit-scrollbar-button": {
      display: "none !important",
      width: "0px",
      height: "0px",
    },
    // For Firefox
    scrollbarWidth: "thin",
    scrollbarColor: `${GREEN_LOGO} ${theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d"}`,
    // For IE and Edge
    msOverflowStyle: "none",
  
    // Ensure horizontal scrolling is enabled
    overflowX: "auto",
    maxWidth: "100%",
  }))

const getDate365DaysAgo = (): Date => {
    const date = new Date();
    date.setDate(date.getDate() - 365);
    return date;
};

const getDateOneMonthAgo = (): Date => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1); 
    return date;
};

const TransactionTable: React.FC = () => {
    const now = new Date();
    const [transactions, setTransactions] = useState<Transactions[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const {t} = useTranslationContext();
    const [dateFrom, setDateFrom] = useState<Date>(getDateOneMonthAgo());
    const [dateTo, setDateTo] = useState<Date>(() => {
        const now = new Date();
        now.setHours(23, 59, 59, 999); // Imposta l'orario alle 23:59:59.999
        return now; // Restituisce l'oggetto Date correttamente inizializzato
      });

    const fetchTransactions = () => {
        getTransactionsDataFilter(page, rowsPerPage, dateFrom, dateTo)
            .then(response => {
                setTransactions(response.transactions);
                setTotalRecords(response.totalRecords);
            })
            .catch(e => {
                console.log(e)
            });
    };

    useEffect(() => {
        if(dateFrom >= dateTo){
            setDateFrom(dayjs(dateTo).add(-1, 'day').toDate())
        }
        fetchTransactions();
    }, [page, rowsPerPage, dateFrom, dateTo]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDateFromChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            setDateFrom(date.toDate()); // Converti in Date
            setPage(0); // Reset to first page on date change
        }
    };

    const handleDateToChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            setDateTo(date.toDate()); // Converti in Date
            setPage(0); // Reset to first page on date change
        }
    };

    return (
        <Box sx={{width:'100%', overflow: 'hidden', maxWidth: '100%', fontFamily:'poppins'}}>
            <Paper sx={{ 
                width: '100%', 
                overflow: 'hidden', 
                // boxShadow:5, 
                borderColor: 'transparent',
                maxWidth: '100%', // Limita la larghezza massima del Paper
                boxSizing: 'border-box',
                // display: 'flex',
                // flexDirection:'column'
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label={t("Data Da")}
                            value={dayjs(dateFrom)}
                            onChange={(date) => handleDateFromChange(date)}
                            disableFuture
                            views={['day', 'month', 'year']}
                            format="DD/MM/YYYY"
                            maxDate={dayjs(dateTo).subtract(1, 'day')}
                            slotProps={{
                                textField: {
                                    required: true,
                                    error: false,
                                    helperText: ''
                                }
                            }}
                            sx={{
                                "& .MuiInputBase-root": {
                                    height: 48,
                                    marginRight: "8px",
                                    color: "grey",
                                },
                                // Default border color
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: GREEN_MAIN,
                                    // borderWidth: "1px",
                                },
                                // Hover state
                                // "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                //     borderColor: GREEN_DARK,
                                //     // borderWidth: "1px",
                                // },
                                // Focused state
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: GREEN_MAIN,
                                    // borderWidth: "2px",
                                },
                                // Label color when focused
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: GREEN_MAIN,
                                },
                                // Calendar icon color
                                "& .MuiSvgIcon-root": {
                                    color: GREEN_MAIN,
                                },
                            }}
                        />
                        <DatePicker
                            label={t("Data A")}
                            value={dayjs(dateTo)}
                            onChange={(date) => handleDateToChange(date)}
                            disableFuture
                            views={['day', 'month', 'year']}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    required: true,
                                    error: false,
                                    helperText: ''
                                }
                            }}
                            sx={{
                                "& .MuiInputBase-root": {
                                    height: 48,
                                    marginRight: "8px",
                                    color: "grey",
                                },
                                // Default border color
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: GREEN_MAIN,
                                    // borderWidth: "1px",
                                },
                                // Hover state
                                // "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                //     borderColor: GREEN_DARK,
                                //     // borderWidth: "1px",
                                // },
                                // Focused state
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: GREEN_MAIN,
                                    // borderWidth: "2px",
                                },
                                // Label color when focused
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: GREEN_MAIN,
                                },
                                // Calendar icon color
                                "& .MuiSvgIcon-root": {
                                    color: GREEN_MAIN,
                                },
                            }}
                        />
                        <IconButton
                            onClick={() => {getPdfTransactions(dateFrom, dateTo).finally()}}
                            sx={{ color: GREEN_MAIN}}
                        >
                            <PrintIcon />
                        </IconButton>
                    </LocalizationProvider>
                </Box>
                <CustomTableContainer>
                    <Table aria-label="transactions table"> {/** stickyHeader */}
                        <TableHead>
                            <TableRow sx={{  
                            }}>
                                <TableCellDefault>{t("Data")}</TableCellDefault>
                                <TableCellDefault>{t("Operazione")}</TableCellDefault>
                                <TableCellDefault>{t("Importo ASSO")}</TableCellDefault>
                                {/* <TableCellDefault>{t("Data di richiesta")}</TableCellDefault> */}
                                <TableCellDefault>{t("Informazioni")}</TableCellDefault>
                                <TableCellDefault>{t("Hash")}</TableCellDefault>
                                <TableCellDefault>{t("Indirizzo Wallet")}</TableCellDefault>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions && transactions.map((transaction) => {
                                console.log('Full Transaction Object:', transaction);
                                return (
                                    <TableRow key={transaction.hash}>
                                        <TableCellRowsDefault
                                            sx={{
                                                color: transaction.dataConfirmed === null ? 'orange' : undefined
                                            }}
                                        >{ transaction.dataConfirmed === null ? "Pending.." : formatDateDayMonthYear(transaction.dataConfirmed)}</TableCellRowsDefault>
                                        <TableCellRowsDefault>{t(transaction.type)}</TableCellRowsDefault>
                                        <TableCellRowsDefault
                                            sx={{
                                                color: transaction.tokensAmount < 0 ? 'red' : undefined
                                            }}
                                        >{transaction.tokensAmount.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCellRowsDefault>
                                        {/* <TableCellRowsDefault>{formatDate(transaction.dataEvent)}</TableCellRowsDefault> */}
                                        <TableCellRowsDefault>
                                            { 
                                            transaction.info?.includes(": ") 
                                                ? `${t(transaction.info.split(": ")[0] + ": ")}${transaction.info.split(": ")[1]}`
                                                : t(transaction.info)
                                            }
                                        </TableCellRowsDefault>
                                        <TableCellRowsDefault>
                                            {transaction.hash ? (
                                                <Link
                                                    href={`${settings.Token.Blockchain.ExplorerUrl}/tx/${transaction.hash}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{color: 'inherit', textDecoration:`none`}}
                                                >
                                                    {transaction.hash}
                                                </Link>
                                            ) : (
                                                <>
                                                    {null}
                                                </>
                                            )}
                                        </TableCellRowsDefault>

                                        <TableCellRowsDefault>
                                            {transaction.addressWallet ? (
                                                <Link
                                                    href={`${settings.Token.Blockchain.ExplorerUrl}/address/${transaction.addressWallet}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{color: 'inherit', textDecoration:`none`}}
                                                >
                                                    {transaction.addressWallet}
                                                </Link>
                                            ) : (
                                                <>
                                                    {null}
                                                </>
                                            )}
                                        </TableCellRowsDefault>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CustomTableContainer>
                <TablePagination
                    labelRowsPerPage={t("Risultati per pagina")}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count}`}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color:GREEN_MAIN,
                        "& .MuiTablePagination-select": {
                            "&:focus": {
                              backgroundColor: "transparent",
                            },
                        },
                        // This targets the dropdown arrow icon
                        "& .MuiTablePagination-selectIcon": {
                            color: GREEN_MAIN,
                        },
                        // This targets the navigation arrows
                        "& .MuiTablePagination-actions": {
                            "& .MuiIconButton-root": {
                            color: GREEN_MAIN,
                            },
                        },
                        // Optional: Style the text to match
                        "& .MuiTablePagination-displayedRows": {
                            color: GREEN_MAIN,
                        },
                        "& .MuiTablePagination-selectLabel": {
                            color: GREEN_MAIN,
                        },
                    }}
                />
            </Paper>
        </Box>
    );
};

export default TransactionTable;